import React, { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const formatLocalDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const CustomDateTimePicker = ({ selectedDate, onDateChange, placeholder = "GG.AA.YYYY SS:DD", label = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(selectedDate ? new Date(selectedDate) : new Date());
    const [selectedTime, setSelectedTime] = useState(selectedDate ? {
        hours: new Date(selectedDate).getHours(),
        minutes: new Date(selectedDate).getMinutes()
    } : { hours: new Date().getHours(), minutes: new Date().getMinutes() });

    const [inputValue, setInputValue] = useState('');

    const datePickerRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (selectedDate) {
            setInputValue(
                new Date(selectedDate).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
            );
            setViewDate(new Date(selectedDate));
            setSelectedTime({
                hours: new Date(selectedDate).getHours(),
                minutes: new Date(selectedDate).getMinutes()
            });
        } else {
            setInputValue('');
        }
    }, [selectedDate]);

    const daysOfWeek = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
    const monthNames = [
        "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
        "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
    ];
    const hoursInDay = Array.from({ length: 24 }, (_, i) => i);
    const minutesInHour = Array.from({ length: 60 }, (_, i) => i);

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let startDayOfWeek = firstDayOfMonth.getDay();
    if (startDayOfWeek === 0) startDayOfWeek = 7;

    const calendarDays = [];
    for (let i = 1; i < startDayOfWeek; i++) {
        calendarDays.push(<div key={`empty-start-${i}`} className="p-1 border border-transparent"></div>);
    }

    const handleDayClick = (e, day) => {
        e.stopPropagation();
        const newDate = new Date(year, month, day);
        newDate.setHours(selectedTime.hours, selectedTime.minutes, 0);
        onDateChange(newDate);
        setIsOpen(false);
    };

    for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const isToday = currentDate.toDateString() === new Date().toDateString();
        const isSelected = selectedDate && (new Date(selectedDate).toDateString() === currentDate.toDateString());

        calendarDays.push(
            <div
                key={day}
                onClick={(e) => handleDayClick(e, day)}
                className={`p-1 cursor-pointer rounded-md text-center text-xs
                            ${isToday ? 'bg-blue-200 dark:bg-blue-700 text-blue-900 dark:text-blue-100' : ''}
                            ${isSelected ? 'bg-blue-600 text-white font-bold' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
                            text-gray-800 dark:text-gray-200 transition-colors duration-100`}
            >
                {day}
            </div>
        );
    }

    const handlePreviousMonth = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setViewDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() - 1);
            return newDate;
        });
    };

    const handleNextMonth = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setViewDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() + 1);
            return newDate;
        });
    };

    const handleTimeChange = (e) => {
        const { name, value } = e.target;
        const newTime = { ...selectedTime, [name]: parseInt(value) };
        setSelectedTime(newTime);
        if (selectedDate) {
            const newDate = new Date(selectedDate);
            newDate.setHours(newTime.hours, newTime.minutes);
            onDateChange(newDate);
        } else {
            const newDate = new Date();
            newDate.setHours(newTime.hours, newTime.minutes);
            onDateChange(newDate);
        }
    };

    const handleInputChange = (e) => {
        const input = e.target;
        let value = input.value;

        const digits = value.replace(/\D/g, '');

        let formattedValue = '';
        if (digits.length > 0) {
            formattedValue += digits.slice(0, 2);
        }
        if (digits.length > 2) {
            formattedValue += '.' + digits.slice(2, 4);
        }
        if (digits.length > 4) {
            formattedValue += '.' + digits.slice(4, 8);
        }
        if (digits.length > 8) {
            formattedValue += ' ' + digits.slice(8, 10);
        }
        if (digits.length > 10) {
            formattedValue += ':' + digits.slice(10, 12);
        }

        setInputValue(formattedValue);

        const regex = /^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})$/;
        if (regex.test(formattedValue)) {
            const match = formattedValue.match(regex);
            const day = parseInt(match[1]);
            const month = parseInt(match[2]) - 1;
            const year = parseInt(match[3]);
            const hours = parseInt(match[4]);
            const minutes = parseInt(match[5]);

            const newDate = new Date(year, month, day, hours, minutes);

            if (newDate instanceof Date && !isNaN(newDate.getTime())) {
                onDateChange(newDate);
                setSelectedTime({ hours: hours, minutes: minutes });
                setViewDate(newDate);
            }
        }
    };

    return (
        <div className="relative w-full" ref={datePickerRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label}
                </label>
            )}
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    placeholder={placeholder}
                    value={inputValue}
                    maxLength="16"
                    onFocus={() => setIsOpen(true)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors cursor-pointer"
                />
                <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-300 pointer-events-none" size={18} />
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg p-3 w-64 max-h-[400px] overflow-y-auto">
                    <div className="flex justify-between items-center mb-3 px-1">
                        <button type="button" onClick={handlePreviousMonth} className="p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                            <ChevronLeft size={16} />
                        </button>
                        <div className="flex items-center space-x-0.5">
                            <select
                                value={month}
                                onChange={(e) => setViewDate(prev => new Date(prev.setMonth(parseInt(e.target.value))))}
                                className="bg-transparent text-sm font-semibold text-gray-800 dark:text-gray-100 py-0.5 px-1 rounded-md cursor-pointer appearance-none text-center"
                            >
                                {monthNames.map((name, index) => <option key={index} value={index}>{name}</option>)}
                            </select>
                            <select
                                value={year}
                                onChange={(e) => setViewDate(prev => new Date(prev.setFullYear(parseInt(e.target.value))))}
                                className="bg-transparent text-sm font-semibold text-gray-800 dark:text-gray-100 py-0.5 px-1 rounded-md cursor-pointer appearance-none text-center"
                            >
                                {Array.from({ length: 111 }, (_, i) => year - 100 + i).map(yr => <option key={yr} value={yr}>{yr}</option>)}
                            </select>
                        </div>
                        <button type="button" onClick={handleNextMonth} className="p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                    <div className="grid grid-cols-7 gap-0.5 text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">
                        {daysOfWeek.map(day => <div key={day} className="text-center">{day}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                        {calendarDays}
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex justify-center items-center space-x-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Saat:</label>
                            <select
                                name="hours"
                                value={selectedTime.hours}
                                onChange={handleTimeChange}
                                className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500 appearance-none hide-scrollbar"
                            >
                                {hoursInDay.map(h => (
                                    <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
                                ))}
                            </select>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Dakika:</label>
                            <select
                                name="minutes"
                                value={selectedTime.minutes}
                                onChange={handleTimeChange}
                                className="bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500 appearance-none hide-scrollbar"
                            >
                                {minutesInHour.map(m => (
                                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomDateTimePicker;